import axios from '../../utils/axios';

// Thin wrappers over the admin axios instance (baseURL .../api, admin token
// attached automatically). All endpoints live under /admin/whatsapp.
const base = '/admin/whatsapp';

// Secondary read calls (status/contacts/templates/tags/settings) keep a hard
// timeout so a stalled backend rejects instead of hanging the UI. The live-chat
// reads — conversations + messages — intentionally have NO timeout so a slightly
// slow thread still renders rather than being cut off mid-load.
const RQ = { timeout: 20000 };

// Every WhatsApp screen renders its OWN specific toast (e.g. "Tag created",
// "Marked 5 unreachable") or updates the UI optimistically. The global axios
// interceptor ALSO toasts on every mutation, which produced a SECOND, generic
// toast. So WhatsApp mutations opt out of the global notifier:
//   • MX  ({ skipNotify: true })       — the screen shows BOTH its own success AND
//     error toast (or intentionally stays silent), so the global one is fully
//     redundant. This is the default for mutations.
//   • MXE ({ skipNotify: 'success' })  — the screen shows NO error toast (it relies
//     on the global one for failure feedback, e.g. send-message / start-chat /
//     edit-broadcast). Suppress only the duplicate SUCCESS toast; keep the global
//     ERROR so nothing ever fails silently.
const MX = { skipNotify: true };
const MXE = { skipNotify: 'success' };

export const waApi = {
  getStatus: () => axios.get(`${base}/status`, RQ).then((r) => r.data),
  registerNumber: (pin) => axios.post(`${base}/register`, { pin }, MX).then((r) => r.data),

  getConversations: (params) => axios.get(`${base}/conversations`, { params }).then((r) => r.data.conversations),
  // No local error toast at the call sites → keep the global error as the net.
  createConversation: (payload) => axios.post(`${base}/conversations`, payload, MXE).then((r) => r.data),
  // Returns { messages, hasMore, nextBefore }. Pass { before, limit } to page
  // older history (cursor is the oldest message id currently held).
  getMessages: (id, params) => axios.get(`${base}/conversations/${id}/messages`, { params }).then((r) => r.data),
  // Inbound media: fetched through the authenticated proxy as a blob (Meta media
  // URLs are short-lived + token-gated, so an <img src> can't load them directly).
  getMessageMedia: (mediaId) => axios.get(`${base}/media/${encodeURIComponent(mediaId)}`, { responseType: 'blob', skipNotify: true }).then((r) => r.data),
  // The sent bubble already shows in the chat; no local error toast → keep global error.
  sendMessage: (id, payload) => {
    if (payload instanceof FormData) {
      return axios.post(`${base}/conversations/${id}/messages`, payload, MXE).then((r) => r.data);
    }
    if (payload && payload.file) {
      const fd = new FormData();
      fd.append('file', payload.file);
      fd.append('type', payload.type);
      if (payload.caption) fd.append('caption', payload.caption);
      if (payload.filename) fd.append('filename', payload.filename);
      // Correlation id so the backend can echo it on the saved message for dedup.
      if (payload.clientId) fd.append('clientId', payload.clientId);
      return axios.post(`${base}/conversations/${id}/messages`, fd, MXE).then((r) => r.data);
    }
    return axios.post(`${base}/conversations/${id}/messages`, payload, MXE).then((r) => r.data);
  },
  deleteMessage: (id, msgId) => axios.delete(`${base}/conversations/${id}/messages/${msgId}`, MX).then((r) => r.data),
  // Background op fired whenever a conversation is opened — never notify.
  markRead: (id) => axios.post(`${base}/conversations/${id}/read`, null, { skipNotify: true }),
  markAllRead: () => axios.post(`${base}/conversations/read-all`, null, { skipNotify: true }).then((r) => r.data),
  sendCallSchedule: (id) => axios.post(`${base}/conversations/${id}/call-schedule`, null, MX).then((r) => r.data),
  setContactStage: (contactId, stage) => axios.post(`${base}/contacts/${contactId}/stage`, { stage }, MX).then((r) => r.data),

  // Returns { contacts, total, optedIn, dbLinked, external, totalDbUsers, filteredTotal }
  getContacts: (params) => axios.get(`${base}/contacts`, { ...RQ, params }).then((r) => r.data),
  getContactsAudit: () => axios.get(`${base}/contacts/audit`, RQ).then((r) => r.data.audit),
  repairContactsAudit: () => axios.post(`${base}/contacts/audit-repair`, null, MX).then((r) => r.data),
  syncAllDbContacts: () => axios.post(`${base}/contacts/sync-all-db`, null, MX).then((r) => r.data),
  updateContact: (id, payload) => axios.put(`${base}/contacts/${id}`, payload, MX).then((r) => r.data.contact),
  importContacts: (file, tags = [], updateExisting = true) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('tags', JSON.stringify(tags));
    fd.append('updateExisting', String(updateExisting));
    return axios.post(`${base}/contacts/import`, fd, MX).then((r) => r.data);
  },
  syncVerified: () => axios.post(`${base}/contacts/sync-verified`, null, MX).then((r) => r.data),
  deleteContact: (id) => axios.delete(`${base}/contacts/${id}`, MX).then((r) => r.data),
  deleteContactsBulk: (ids) => axios.post(`${base}/contacts/delete-bulk`, { ids }, MX).then((r) => r.data),
  // Optimistic chip toggles whose call sites swallow errors (.catch(()=>{})) →
  // keep the global error net so a failed toggle isn't completely silent.
  addContactTag: (id, tag) => axios.post(`${base}/contacts/${id}/tags`, { tag }, MXE),
  removeContactTag: (id, tag) => axios.delete(`${base}/contacts/${id}/tags/${encodeURIComponent(tag)}`, MXE),

  getTags: () => axios.get(`${base}/tags`, RQ).then((r) => r.data.tags),
  createTag: (payload) => axios.post(`${base}/tags`, payload, MX).then((r) => r.data.tag),
  deleteTag: (id) => axios.delete(`${base}/tags/${id}`, MX),

  getTemplates: () => axios.get(`${base}/templates`, RQ).then((r) => r.data.templates),
  // Authoritative two-way sync with Meta: pulls platform edits into our DB and
  // prunes templates deleted on Meta. Returns { synced, summary, templates, msg }.
  syncTemplates: () => axios.post(`${base}/templates/sync`, null, MX).then((r) => r.data),
  createTemplate: (payload) => axios.post(`${base}/templates`, payload, MX).then((r) => r.data.template),
  updateTemplate: (id, payload) => axios.put(`${base}/templates/${id}`, payload, MX).then((r) => r.data.template),
  deleteTemplate: (id) => axios.delete(`${base}/templates/${id}`, MX),
  testTemplate: (id, phone, variables = {}) => axios.post(`${base}/templates/${id}/test`, { phone, variables }, MX).then((r) => r.data),

  getBroadcasts: () => axios.get(`${base}/broadcasts`, RQ).then((r) => r.data.broadcasts),
  getAudiences: () => axios.get(`${base}/audiences`, RQ).then((r) => r.data.audiences),
  createBroadcast: (payload) => axios.post(`${base}/broadcasts`, payload, MX).then((r) => r.data.broadcast),
  // Edit a not-yet-sent (scheduled/draft) broadcast: name, template, audience,
  // schedule, skip toggle. Once sending, only the name is editable (backend-enforced).
  // handleSave has no local error toast → keep the global error net (MXE).
  updateBroadcast: (id, payload) => axios.put(`${base}/broadcasts/${id}`, payload, MXE).then((r) => r.data.broadcast),
  // Live reach preview: { total, willReceive, skipped } after opt-outs, dedupe
  // and (optional) same-template suppression. Read-style POST fired on every
  // keystroke — opt out of the global success/error notifications.
  previewBroadcast: (payload) => axios.post(`${base}/broadcasts/preview`, payload, { ...RQ, skipNotify: true }).then((r) => r.data),
  pauseBroadcast: (id) => axios.post(`${base}/broadcasts/${id}/pause`, null, MX).then((r) => r.data.broadcast),
  resumeBroadcast: (id) => axios.post(`${base}/broadcasts/${id}/resume`, null, MX).then((r) => r.data.broadcast),
  restartBroadcast: (id) => axios.post(`${base}/broadcasts/${id}/restart`, null, MX).then((r) => r.data.broadcast),
  deleteBroadcast: (id) => axios.delete(`${base}/broadcasts/${id}`, MX),
  getBroadcastFailed: (id) => axios.get(`${base}/broadcasts/${id}/failed`, RQ).then((r) => r.data),
  removeBroadcastFailed: (id) => axios.post(`${base}/broadcasts/${id}/remove-failed`, null, MX).then((r) => r.data),

  getSettings: () => axios.get(`${base}/settings`, RQ).then((r) => r.data),
  getCost: () => axios.get(`${base}/cost`, RQ).then((r) => r.data),
  getMessagingUsage: () => axios.get(`${base}/messaging-usage`, RQ).then((r) => r.data),
  // Combined growth analytics (daily series + totals + period-over-period growth
  // + tier headroom). Hits several Meta endpoints, so a longer timeout.
  getAnalytics: (days = 30) => axios.get(`${base}/analytics`, { ...RQ, timeout: 30000, params: { days } }).then((r) => r.data),
  updateSettings: (payload) => axios.put(`${base}/settings`, payload, MX).then((r) => r.data.settings),
  getAiSuggestedReply: (contactId, agentInstruction = "") => axios.post(`${base}/ai/suggest`, { contactId, agentInstruction }, MX).then((r) => r.data),
  getAiChatSummary: (contactId) => axios.post(`${base}/ai/summarize`, { contactId }, MX).then((r) => r.data),
  getKeywordAnalytics: (days = 30) => axios.get(`${base}/keywords/analytics`, { ...RQ, params: { days } }).then((r) => r.data),
  getUnmatchedQueries: (days = 30, limit = 50) => axios.get(`${base}/keywords/unmatched`, { ...RQ, params: { days, limit } }).then((r) => r.data),
  convertUnmatchedToRule: (payload) => axios.post(`${base}/keywords/convert-to-rule`, payload, MX).then((r) => r.data),

  // WhatsApp Follow-up Automation (Pending Business, Pending Product, Unverified)
  getFollowUpAutomationStatus: () => axios.get(`${base}/followup-automation/status`, RQ).then((r) => r.data),
  updateFollowUpAutomationConfig: (payload) => axios.put(`${base}/followup-automation/config`, payload, MX).then((r) => r.data),
  runFollowUpAutomation: (payload) => axios.post(`${base}/followup-automation/run`, payload, MX).then((r) => r.data),
  testFollowUpMessage: (payload) => axios.post(`${base}/followup-automation/test`, payload, MX).then((r) => r.data),
  getFollowUpAutomationLogs: (params) => axios.get(`${base}/followup-automation/logs`, { ...RQ, params }).then((r) => r.data),
};

export default waApi;
